<template>
  <div class="modal-backdrop">
    <div class="modal-container">
      <div class="modal">
        <div class="wrapper">
          <div class="header">
            <h2>Save filter</h2>
            <div @click="onClose">
              <Icon name="times" size="24" />
            </div>
          </div>
          <div v-if="isFilterUpdate" class="body">
            <Select
              id="saved-filter"
              :options="savedFilters"
              :value="selectedExistingFilter"
              :required="true"
              :error="hasValidationError"
              error-message="Select a filter to apply"
              label="Modify saved filter"
              @input="handleFilterChange($event)"
            />
            <div class="body__switch_view">
              <a @click.prevent="handleIsUpdate(false)">Create new filter</a>
            </div>
          </div>
          <div v-else class="body">
            <text-input
              v-model="selectedFilterName"
              :show-clear-btn="true"
              :value="selectedFilterName"
              :required="true"
              :error="hasValidationError"
              error-message="Provide a name for filter"
              placeholder="Provide a name for the filter"
              label="Filter name"
            />
            <div class="body__switch_view">
              <a @click.prevent="handleIsUpdate(true)">Update saved filter</a>
            </div>
          </div>

          <div class="footer">
            <div class="rCol">
              <Button
                label="Cancel"
                appearance="tertiary"
                class="button-group__button"
                :small="true"
                @onClick="onClose"
              />
              <Button
                class="button-group__button"
                label="Apply"
                :small="true"
                @onClick="onSave"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// External libraries
import ClickOutside from "vue-click-outside";
// Common Component
import { Button, Icon, TextInput, Select } from "../../components";

export default {
  name: "SaveNewFilterModal",
  directives: {
    ClickOutside,
  },
  components: {
    Button,
    TextInput,
    Select,
    Icon,
  },
  props: {
    /**
     * @description
     * List of saved filters
     */
    savedFilters: { type: Array, default: () => {} },
    /**
     * @description
     * If isUpdate is true then saved filter list Select dropdown will be shown to override the existing filter instead of text field with new filter name
     */
    isUpdate: { type: Boolean, default: true },
  },
  data() {
    return {
      selectedFilterName: "",
      selectedExistingFilter: this.savedFilters?.length
        ? this.savedFilters[0]
        : null,
      isFilterUpdate: this.isUpdate,
      hasValidationError: false,
    };
  },
  watch: {
    /**
     * @description
     * Set isFilterUpdate value whenever isUpdate prop is updated
     * @param {boolean} val - sets isFilterUpdate
     */
    isUpdate(val) {
      this.isFilterUpdate = val;
    },
  },
  methods: {
    /**
     * @description
     * Emits onModalClose callback on close of the modal
     */
    onClose() {
      this.$emit("onModalClose");
    },
    /**
     * @description
     * Sets the selected existing filter,
     * Resets the validation error
     * @param {object} selectedFilter - selected filter object
     */
    handleFilterChange(selectedFilter) {
      this.selectedExistingFilter = selectedFilter;
      this.hasValidationError = false;
    },
    /**
     * @description
     * Sets the selected existing filter,
     * Resets the validation error
     * @param {object} status - selected filter object
     */
    handleIsUpdate(status) {
      this.isFilterUpdate = status;
      this.hasValidationError = false;
    },
    /**
     * @description
     * Saves the filter if there is no validation error
     * Emits onSaveNewFilter method defined on the parent(FilterBar) component,
     * Triggers toggleSaveNewFilterModal method defined on the parent(FilterBar) to close the modal,
     * @param {object} selectedFilter - selected filter object
     */
    onSave() {
      if (this.isFilterUpdate) {
        if (this.selectedExistingFilter) {
          this.hasValidationError = false;
          this.$parent.$emit(
            "onSaveNewFilter",
            this.selectedExistingFilter.value,
            this.isFilterUpdate
          );
          this.$parent.toggleSaveNewFilterModal();
        } else {
          this.hasValidationError = true;
        }
      } else {
        if (this.selectedFilterName) {
          this.hasValidationError = false;
          this.$parent.$emit(
            "onSaveNewFilter",
            this.selectedFilterName,
            this.isFilterUpdate
          );
          this.$parent.toggleSaveNewFilterModal();
        } else {
          this.hasValidationError = true;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/imports.scss";

.modal-fade-enter,
.modal-fade-leave-active {
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.5s ease;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.modal-container {
  max-width: 400px;
  width: 100%;
  overflow-x: auto;
  border-radius: 5px;

  & .modal {
    box-shadow: 2px 2px 20px 1px;
    display: flex;
    flex-direction: column;
  }
  @include media("<=720px") {
    position: absolute;
    bottom: 0;
  }
}

.modal-backdrop {
  @include media("<=desktop") {
    left: 0;
    bottom: 0;
    top: auto;
    right: 0;
  }

  &:focus {
    outline: none;
  }

  & .wrapper {
    background-color: var(--Gray-50);
    padding-top: 24px;

    @include media("<=desktop") {
      padding: 10px 4px;
    }

    @include media("<=720px") {
      padding: 10px 4px;
    }

    & .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 24px;

      & h2 {
        color: var(--Blue-900-Darkest);
        font-size: 18px;
        font-family: "Maersk Text Bold";
        @include media("<720px") {
          margin: 10px 10px;
        }
      }
      & i {
        font-size: 24px;
        font-style: normal;
        cursor: pointer;
      }
    }

    & .body {
      padding: 24px;
      & .body__switch_view {
        justify-content: flex-end;
        display: flex;
        & a {
          font-size: 14px;
          margin-top: 16px;
          color: var(--Blue-600-Dark);
          cursor: pointer;
        }
      }
    }

    & .footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 0 24px;
      padding-bottom: 16px;
      background-color: var(--Gray-100);

      @include media("<720px") {
        margin: 10px 10px;
      }

      & .lCol {
        span {
          font-size: 14px;
          &:hover {
            cursor: pointer;
          }
        }
      }

      & .rCol {
        display: flex;
      }
    }
  }
}
</style>
